import { Fragment, type ReactNode } from 'react'
import { Menu, ChevronRight, ChevronLeft } from 'lucide-react'
import { useRouter } from '@tanstack/react-router'
import { useAppStore } from '../../stores/appStore'

export interface Crumb {
  label: string
  onClick?: () => void
}

// PageHeader is a breadcrumb bar: parent › child › child. Every non-terminal
// crumb is clickable (gray); the terminal crumb is black. A back arrow steps
// through browser history (not the breadcrumb parent).
export function PageHeader({ crumbs, actions }: { crumbs: Crumb[]; actions?: ReactNode }) {
  const setSidebarOpen = useAppStore((s) => s.setSidebarOpen)
  const router = useRouter()
  return (
    <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border-default/50 px-4 md:px-6">
      <div className="flex min-w-0 items-center gap-1.5">
        <button
          onClick={() => router.history.back()}
          className="-ml-2 shrink-0 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          aria-label="Go back"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => setSidebarOpen(true)}
          className="shrink-0 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 md:hidden"
          aria-label="Open sidebar"
        >
          <Menu size={20} />
        </button>
        {[{ label: 'Home', onClick: () => router.navigate({ to: '/' }) }, ...crumbs].map((c, i, all) => {
          const last = i === all.length - 1
          return (
            <Fragment key={i}>
              {i > 0 && <ChevronRight size={16} className="shrink-0 text-gray-300" />}
              {last || !c.onClick ? (
                <span className="truncate text-[15px] font-semibold text-gray-900">{c.label}</span>
              ) : (
                <button
                  onClick={c.onClick}
                  className="shrink-0 text-[15px] text-gray-500 transition-colors hover:text-gray-900"
                >
                  {c.label}
                </button>
              )}
            </Fragment>
          )
        })}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  )
}
